import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Building2, ArrowRight, Menu, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const T = {
  bg: '#050A30', bgAlt: '#0A1240', bgDeep: '#020818',
  cyan: '#00FFFF', gold: '#FBBF24', white: '#FFFFFF',
  textMuted: '#A0A8C8', cardBorder: 'rgba(0,255,255,0.12)',
  font: {
    display: "'Barlow Condensed', sans-serif",
    heading: "'Barlow', sans-serif",
    body: "'Inter', sans-serif",
  }
};

export default function AgenciasPage() {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/public/agencies`)
      .then(r => r.json())
      .then(d => { setAgencies(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div style={{ background: T.bg, fontFamily: T.font.body, color: T.white }} className="min-h-screen flex flex-col">
      <Helmet>
        <title>Agencias Verificadas | AutoFlow</title>
        <meta property="og:title" content="Agencias Verificadas | AutoFlow" />
        <meta property="og:description" content="Todas las agencias que forman parte de AutoFlow" />
      </Helmet>

      {/* HEADER */}
      <header className="sticky top-0 z-50" style={{ background: 'rgba(5,10,48,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,255,255,0.08)' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="agencias-logo">
            <img src="/logo.png" alt="AutoFlow" className="h-9 w-auto rounded-lg" />
            <span style={{ fontFamily: T.font.display, fontWeight: 800, fontSize: '1.3rem' }}>Auto<span style={{ color: T.cyan }}>Flow</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/autos-flow" className="text-sm font-bold transition-colors hover:opacity-80" style={{ color: T.cyan }} data-testid="nav-autos-flow">Autos - Flow</Link>
            <Link to="/agencias" className="text-sm font-medium" style={{ color: T.white }}>Agencias</Link>
            <Link to="/" className="text-sm" style={{ color: T.textMuted }}>Inicio</Link>
            <Link to="/app/login" className="text-sm px-5 py-2 rounded-full transition-all hover:opacity-90" style={{ border: `1.5px solid ${T.cyan}`, color: T.cyan }}>Ingresar</Link>
          </nav>
          <button className="md:hidden p-2 min-h-[44px] min-w-[44px]" onClick={() => setMenuOpen(!menuOpen)} data-testid="agencias-mobile-menu">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden px-5 py-4 space-y-3" style={{ background: 'rgba(5,10,48,0.97)', borderBottom: `1px solid ${T.cardBorder}` }}>
            <Link to="/autos-flow" className="block py-2 font-bold" style={{ color: T.cyan }}>Autos - Flow</Link>
            <Link to="/agencias" className="block py-2 font-medium" style={{ color: T.white }}>Agencias</Link>
            <Link to="/" className="block py-2" style={{ color: T.textMuted }}>Inicio</Link>
            <Link to="/app/login" className="block text-center py-3 rounded-full font-semibold mt-2" style={{ background: T.cyan, color: T.bg }}>Ingresar</Link>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="py-16 md:py-24 text-center">
        <div className="max-w-4xl mx-auto px-5">
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4"
            style={{ fontFamily: T.font.display, fontWeight: 800 }}
          >
            Agencias Verificadas
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}
            className="text-base md:text-lg" style={{ color: T.textMuted }}
          >
            Todas las agencias que forman parte de AutoFlow
          </motion.p>
        </div>
      </section>

      {/* GRID */}
      <section className="pb-20 md:pb-28 flex-1">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          {loading ? (
            <div className="text-center py-16" style={{ color: T.textMuted }}>
              <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              Cargando agencias...
            </div>
          ) : agencies.length === 0 ? (
            <div className="text-center py-16">
              <Building2 className="mx-auto mb-4 opacity-30" size={48} />
              <p style={{ color: T.textMuted }}>No hay agencias disponibles</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="agencies-grid">
              {agencies.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04, duration: 0.4 }}
                  className="group"
                >
                  <div
                    className="rounded-2xl p-5 md:p-6 flex flex-col items-center text-center h-full transition-all duration-300 group-hover:scale-[1.03]"
                    style={{ background: 'linear-gradient(160deg, #0D1A50 0%, #050A30 100%)', border: `1px solid ${T.cardBorder}` }}
                    data-testid={`agency-card-${a.slug}`}
                  >
                    {a.logoUrl ? (
                      <img
                        src={a.logoUrl} alt={a.name}
                        className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 mb-4"
                        style={{ borderColor: T.cyan }}
                      />
                    ) : (
                      <div
                        className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center mb-4 border-2"
                        style={{ background: 'rgba(0,255,255,0.06)', borderColor: 'rgba(0,255,255,0.3)' }}
                      >
                        <Building2 size={32} color={T.cyan} />
                      </div>
                    )}
                    <h3 className="text-sm md:text-base font-bold mb-1 truncate w-full" style={{ fontFamily: T.font.heading }}>{a.displayName || a.name}</h3>
                    {a.description && (
                      <p className="text-xs mb-4 line-clamp-2 leading-relaxed" style={{ color: T.textMuted }}>{a.description}</p>
                    )}
                    <Link
                      to={`/a/${a.slug}`}
                      className="mt-auto inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-xs md:text-sm font-semibold transition-all duration-300 hover:shadow-lg hover:brightness-110"
                      style={{ background: T.gold, color: '#1a1a2e' }}
                      data-testid={`view-agency-${a.slug}`}
                    >
                      Ver agencia <ArrowRight size={14} />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 border-t" style={{ background: T.bgDeep, borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs" style={{ color: T.textMuted }}>&copy; {new Date().getFullYear()} AutoFlow</span>
          <div className="flex items-center gap-4 text-xs" style={{ color: T.textMuted }}>
            <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
            <Link to="/autos-flow" className="hover:text-white transition-colors">Autos - Flow</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacidad</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terminos</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
